import { useCallback, useState } from "react";
import { useApi } from "./useApi";
import { apiRequest, ApiError } from "src/services/apiClient";

export type SumupTarget = "own" | "praxis";
export type SumupSwitch = "portal" | "deskQr" | "terminal";

export interface SumupReader {
    id: string;
    name: string;
    model: string | null;
}

export interface SumupConnectionSummary {
    connected: boolean;
    merchantCode?: string;
    merchantName?: string | null;
    needsReconnect?: boolean;
    connectedAt?: string;
    /** The account granted card-terminal access (false = connected before terminals, reconnect needed). */
    terminals?: boolean;
    /** Paired SumUp Solo card terminals. */
    readers?: SumupReader[];
}

export interface SumupStatus {
    /** false until Medinvoice's SumUp OAuth app is live on this server */
    configured: boolean;
    canManagePraxis: boolean;
    /** Card terminals (SumUp Solo) are available on this server. */
    terminalsConfigured: boolean;
    /** Someone in the praxis has paired a card terminal (any account). */
    praxisHasReaders: boolean;
    own: SumupConnectionSummary;
    praxis: SumupConnectionSummary;
    /** Praxis-wide switches (Admin changes them): "Mit Karte bezahlen" in the portal, the desk QR, the card terminal. */
    portalEnabled: boolean;
    deskQrEnabled: boolean;
    terminalEnabled: boolean;
    /** This member's invoices go to the praxis account instead of their own (one or the other). */
    usePraxis: boolean;
}

export interface SumupPayment {
    id: string;
    invoiceId: string;
    readableId: string | null;
    recipientName: string | null;
    patientName: string | null;
    amount: number;
    paidAt: string;
    transactionCode: string | null;
    /** Doctor whose SumUp account received it, or "praxis" for the practice account. */
    account: string | null;
    merchantName: string | null;
}

/** Booked card payments for the Bank page — `available` is false until anyone connected SumUp. */
export function useSumupPayments(enabled: boolean) {
    const { data, loading } = useApi<{ available: boolean; payments: SumupPayment[] }>(enabled ? "/sumup/payments" : null, 1);
    return { available: data?.available ?? false, payments: data?.payments ?? [], loading };
}

export function useSumup() {
    const [trigger, setTrigger] = useState(0);
    const { data, loading } = useApi<SumupStatus>("/sumup/status", trigger + 1);
    const refresh = useCallback(() => setTrigger((t) => t + 1), []);

    /** Returns SumUp's authorize URL to send the browser to. */
    const connect = useCallback(async (target: SumupTarget): Promise<string> => {
        const res = await apiRequest<{ url: string }>(
            "/sumup/connect",
            { method: "POST", body: JSON.stringify({ target }) },
            { "Content-Type": "application/json" },
        );
        return res.url;
    }, []);

    /** Finishes the OAuth flow after SumUp redirected back with ?code&state. */
    const finishConnect = useCallback(async (code: string, state: string): Promise<{ ok: true } | { ok: false; code?: string }> => {
        try {
            await apiRequest(
                "/sumup/callback",
                { method: "POST", body: JSON.stringify({ code, state }) },
                { "Content-Type": "application/json" },
            );
            refresh();
            return { ok: true };
        } catch (e) {
            return { ok: false, code: e instanceof ApiError ? e.body?.code : undefined };
        }
    }, [refresh]);

    const disconnect = useCallback(async (target: SumupTarget) => {
        await apiRequest(`/sumup/connection?target=${target}`, { method: "DELETE" });
        refresh();
    }, [refresh]);

    const setSwitches = useCallback(async (switches: Partial<Record<SumupSwitch, boolean>>) => {
        await apiRequest(
            "/sumup/switches",
            { method: "PATCH", body: JSON.stringify(switches) },
            { "Content-Type": "application/json" },
        );
        refresh();
    }, [refresh]);

    const setUsePraxis = useCallback(async (enabled: boolean) => {
        await apiRequest(
            "/sumup/use-praxis",
            { method: "PATCH", body: JSON.stringify({ enabled }) },
            { "Content-Type": "application/json" },
        );
        refresh();
    }, [refresh]);

    /** Pairs a Solo with the account; resolves to SumUp's error code on failure (INVALID_CODE, PAIRING_FAILED, RECONNECT_NEEDED). */
    const pairReader = useCallback(async (target: SumupTarget, pairingCode: string, name: string): Promise<{ ok: true } | { ok: false; code?: string }> => {
        try {
            await apiRequest(
                "/sumup/readers",
                { method: "POST", body: JSON.stringify({ target, pairingCode, name }) },
                { "Content-Type": "application/json" },
            );
            refresh();
            return { ok: true };
        } catch (e) {
            return { ok: false, code: e instanceof ApiError ? e.body?.code : undefined };
        }
    }, [refresh]);

    const unpairReader = useCallback(async (readerId: string) => {
        await apiRequest(`/sumup/readers/${readerId}`, { method: "DELETE" });
        refresh();
    }, [refresh]);

    return { status: data, loading, connect, finishConnect, disconnect, setSwitches, setUsePraxis, pairReader, unpairReader };
}
